import type { ChapterData, Meta, Ref } from './types';

/** Origin of the chapter API. Empty means same-origin, which is what the dev
 *  proxy relies on. Baked in at build time, so it is public, not secret. */
const API_BASE = (import.meta.env.VITE_BIBLE_API ?? '').replace(/\/$/, '');

/** Chapters are immutable, so a process-lifetime cache is safe and makes
 *  flipping between columns and translations feel instant. */
const chapterCache = new Map<string, Promise<ChapterData>>();

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? `Request failed (${res.status})`);
  }
  return res.json();
}

export const fetchMeta = () => get<Meta>('/api/meta');

export function fetchChapter(ref: Ref): Promise<ChapterData> {
  const key = `${ref.translation}/${ref.book}/${ref.chapter}`;
  let pending = chapterCache.get(key);
  if (!pending) {
    pending = get<ChapterData>(`/api/chapter/${key}`);
    // Don't cache failures — a transient error shouldn't poison the ref forever.
    pending.catch(() => chapterCache.delete(key));
    chapterCache.set(key, pending);
  }
  return pending;
}

/** Warm the cache without blocking render — used to preload adjacent chapters. */
export const prefetchChapter = (ref: Ref) => void fetchChapter(ref).catch(() => {});
